import * as React from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { ActivityList } from "./ActivityList";
import { FinishAdjustmentDialog } from "./FinishAdjustmentDialog";
import { useNewTaskStatusMutations } from "../hooks";
import { adjustmentFinishBlockKey } from "../utils";
import type { AdjustmentTask, Task } from "../types";

interface AdjustmentActivitiesProps {
  adjustment: AdjustmentTask;
  currentUserId?: number | undefined;
  defaultExpanded?: boolean;
  /**
   * Parent-level delete authority of the caller (caller computes: MD → parent not
   * terminal; non-MD → canCreateAdjustment(role, parent.status)). The per-card gate
   * additionally requires ownership for non-MD — see `isMd`.
   */
  canDelete?: boolean;
  /** True when the caller is a global MD — MD may delete any adjustment, not only their own. */
  isMd?: boolean;
  onFinishSuccess?: () => void;
  onDeleteSuccess?: () => void;
}

/**
 * A single adjustment as a collapsible card with its activities.
 * Used by both AdjustmentsListModal (popup view) and TaskExpandedRow (inline).
 */
export function AdjustmentActivities({
  adjustment,
  currentUserId,
  defaultExpanded = false,
  canDelete = false,
  isMd = false,
  onFinishSuccess,
  onDeleteSuccess,
}: AdjustmentActivitiesProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const activities = adjustment.activities ?? [];
  const { deleteAdjustmentMutation } = useNewTaskStatusMutations();

  const isOwnedByViewer =
    currentUserId != null && adjustment.assignedStaffId === currentUserId;
  const isUnfinished = adjustment.adjustmentPercent == null;
  const showFinish = isOwnedByViewer && isUnfinished;
  const finishBlockKey = adjustmentFinishBlockKey(activities);
  // The owner may EDIT the finishing result (percent + comment) of an already-
  // finished adjustment while the task is still in-flight. Once the task is
  // approved/billed (DONE / BILLED) the result is locked. The adjustment mirrors
  // the parent's status, so we read it off the adjustment row.
  const isTerminalParent =
    adjustment.newStatus === "DONE" || adjustment.newStatus === "BILLED";
  const showEditFinished = isOwnedByViewer && !isUnfinished && !isTerminalParent;
  // Non-MD callers may only delete their OWN adjustment; MD may delete any.
  const showDelete = canDelete && (isMd || isOwnedByViewer);
  const hasActions = showFinish || showEditFinished || showDelete;

  const ownerName = adjustment.assignedStaff?.name ?? t("unknownUser");
  const initial = ownerName.trim().charAt(0).toUpperCase() || "?";

  const handleDelete = () => {
    deleteAdjustmentMutation.mutate(adjustment.id, {
      onSuccess: () => {
        setDeleteOpen(false);
        onDeleteSuccess?.();
      },
    });
  };

  const adjustmentAsTask: Task = {
    id: adjustment.id,
    newStatus: adjustment.newStatus,
    isAdjustment: true,
    assignedStaffId: adjustment.assignedStaffId,
    adjustmentPercent: adjustment.adjustmentPercent ?? null,
    activities: adjustment.activities,
    createdAt: adjustment.createdAt,
  };

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Owner + meta — clicking toggles the activity list */}
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          onClick={() => setExpanded((p) => !p)}
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
          )}
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700">
            {initial}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium text-slate-700">
                {ownerName}
              </span>
              {adjustment.adjustmentPercent != null ? (
                <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                  {adjustment.adjustmentPercent}% {t("adjustment.changed")}
                </span>
              ) : (
                <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-600">
                  {t("adjustment.pending")}
                </span>
              )}
            </div>
            <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-[11px]">
              <span>
                {activities.length} {t("activities")}
              </span>
              {adjustment.createdAt && (
                <>
                  <span aria-hidden>·</span>
                  <span>
                    {format(new Date(adjustment.createdAt), "MMM d, yyyy")}
                  </span>
                </>
              )}
            </div>
          </div>
        </button>

        {/* Actions */}
        {hasActions && (
          <div
            className="flex shrink-0 items-center gap-1.5"
            onClick={(e) => e.stopPropagation()}
          >
            {showFinish && (
              <FinishAdjustmentDialog
                task={adjustmentAsTask}
                disabledReason={finishBlockKey ? t(finishBlockKey) : undefined}
                onSuccess={onFinishSuccess}
              />
            )}
            {showEditFinished && (
              <FinishAdjustmentDialog
                task={adjustmentAsTask}
                mode="edit"
                initialPercent={adjustment.adjustmentPercent ?? 0}
                initialComment={adjustment.comment ?? ""}
                onSuccess={onFinishSuccess}
              />
            )}
            {showDelete && (
              <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                    aria-label={t("adjustment.delete")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t("adjustment.deleteTitle")}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("adjustment.deleteDescription")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      disabled={deleteAdjustmentMutation.isPending}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {deleteAdjustmentMutation.isPending
                        ? t("deleting")
                        : t("delete")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </div>

      {expanded && (
        <div className={cn("border-t border-slate-100")}>
          <ActivityList activities={activities} />
        </div>
      )}
    </div>
  );
}
